import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';

import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import { Colors } from 'shared/assets/root-assets/colors';

import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import { GetMatingBatchReportGraphBySiteAndDate } from 'shared/state/ducks/mating-batch-report/operations';
import { LineChart } from '../graph/line-graph/line-chart';
import { Heading } from '../utils/heading';
import { isSameDate } from 'shared/helpers/date-helpers';

interface PropsFromParent {
	closeModal: () => void;
	date: Date;
}

export const BatchReportFarrowingLineGraph: FC<PropsFromParent> = React.memo(({ closeModal, date }) => {
	const dispatch = useDispatch();
	// report data
	const farrowingReport = useSelector(
		(state: WebAppState) => state.matingBatchReports.matingBatchReportsGraph.farrowingReport,
	);
	const { loadingGraph } = useSelector((state: WebAppState) => state.matingBatchReports);
	const matingBathces = useSelector((state: WebAppState) => state.matingBatches.entities);
	const { siteId } = useSelector((state: WebAppState) => state.profile.active!);
	useEffect(() => {
		if (siteId) {
			dispatch(GetMatingBatchReportGraphBySiteAndDate(date, siteId));
		}
	}, [siteId]);

	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'weekYear',
				valueField: 'totalFarrowings',
				name: 'totalFarrowings',
				color: Colors.yellow,
				key: 'totalFarrowings',
				scaleName: 'farrowings',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'avgAlive',
				name: 'avgAlive',
				color: Colors.pastelBlue,
				key: 'avgAlive',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'avgLivebornFirstParity',
				name: 'avgLivebornFirstParity',
				color: Colors.pastelGreen,
				key: 'avgLivebornFirstParity',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'avgDead',
				name: 'avgDead',
				color: Colors.pastelRed,
				key: 'avgDead',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'TotalBornAvg',
				name: 'TotalBornAvg',
				color: Colors.pastelOrange,
				key: 'TotalBornAvg',
				scaleName: 'pcs',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [
			{ position: 'right', scaleName: 'farrowings', showGrid: false } as RawAxisProps,
			{ position: 'left', scaleName: 'pcs' } as RawAxisProps,
		];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		if (!farrowingReport) {
			return [];
		}

		let data: any[] = [];

		farrowingReport.forEach(element => {
			let objectData = { ...element } as any;

			const date = moment(element.matingBatch?.matingPeriodStart)
			const batch =  matingBathces.find(x => isSameDate(x.matingPeriodStart , element.matingBatch?.matingPeriodStart));
			const key = batch?.batchNumber + '-' + date.year();

			objectData.weekYear = key;
			objectData.date = date;

			objectData.TotalBornAvg = Math.round((objectData.AvgAlive + objectData.AvgDead) * 10) / 10;
			data.push(objectData);
		});

		return data.slice().sort((a, b) => a.date - b.date);
	}, [farrowingReport]);

	return (
		<PageContainer>
			<Heading text={localized('Farrowings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>
				{loadingGraph ? (
					<ViewWeb className="spinner-container-active">
						<ClipLoader color="#f2ac40" size={70} />
					</ViewWeb>
				) : (
					<ViewWeb className="flex-row">
						<ViewWeb className="vertical-legend-title">
							<div className="rotate-container">
								<WhiteText className="header-white-text rotate-270">{localized('PigsPcs')}</WhiteText>
							</div>
						</ViewWeb>
						<ViewWeb className="flexOne">
							<LineChart
								lineSeries={series}
								chartData={data}
								valueAxis={yAxis}
								title=""
								yTitle={localized('FarrowingsPerBatchPcs')}
								keyTranslator={localized}
								zoomPan="both"
								verticalArgumentLabels={true}
							/>
							<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
						</ViewWeb>
					</ViewWeb>
				)}
			</ViewWeb>
		</PageContainer>
	);
});
